/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/store/makeStore.js
 *
 * Purpose: Factory that creates a React Context-based store (provider + reducer) with automatic
 *     session-storage persistence, used to build feature-specific stores.
 *
 * Scope: shared store-creation utility underlying feature stores such as the App store and the
 *     Order request store.
 *
 * Tags: customer-portal
 *
 * Overview: Given a reducer, initial state, a storage key, and an optional lazy-init function,
 *     builds a separate dispatch context and state context, seeding the initial state from
 *     sessionStorage under key when a value is present there. Wraps the reducer so every
 *     dispatched action's resulting state is persisted back to sessionStorage via
 *     safeSessionStorageSet. Returns a tuple of [StoreProvider, useStore, useDispatch] for
 *     consumers to compose their own store hooks.
 *
 * Exports: default makeStore factory function.
 */

import { createContext, useContext, useReducer } from 'react';
import { safeSessionStorageSet } from 'shared/utils/functions';

const makeStore = (userReducer, initialState, key, init) => {
  const dispatchContext = createContext(undefined);
  dispatchContext.displayName = `${key}Dispatch`;
  const storeContext = createContext(undefined);
  storeContext.displayName = `${key}Context`;

  try {
    initialState = JSON.parse(sessionStorage.getItem(key)) || initialState;
  } catch {}

  const reducer = (state, action) => {
    const newState = userReducer(state, action);
    safeSessionStorageSet(key, JSON.stringify(newState));
    return newState;
  };

  const StoreProvider = ({ children }) => {
    const [store, dispatch] = useReducer(reducer, initialState, init);

    return (
      <dispatchContext.Provider value={dispatch}>
        <storeContext.Provider value={store}>{children}</storeContext.Provider>
      </dispatchContext.Provider>
    );
  };

  const useDispatch = () => {
    return useContext(dispatchContext);
  };
  const useStore = () => {
    return useContext(storeContext);
  };

  return [StoreProvider, useStore, useDispatch];
};

export default makeStore;
